import { useEffect, useState } from "react";
import {
  Calendar, CircleDollarSign, Wallet, PiggyBank, Coins, Zap, Users, ArrowRightLeft, Loader2, X,
} from "lucide-react";
import { myApi } from "../api";
import Pagination from "../components/Pagination";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat("pt-PT", { maximumFractionDigits: 2 }).format(v || 0);

export default function MyCommissions() {
  const [resumo, setResumo] = useState(null);
  const [pagData, setPagData] = useState(null);
  const [acum, setAcum] = useState(null);
  const [pagPage, setPagPage] = useState(1);
  const pagSize = 10;

  // Transfer to credits modal state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferValor, setTransferValor] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");

  const refreshAll = () => {
    myApi.comissoesResumo().then(setResumo).catch(() => setResumo([]));
    myApi.comissoesAcumulado().then(setAcum).catch(() => setAcum({}));
    setPagData(null);
    myApi.comissoesPagamentos(pagPage, pagSize)
      .then(setPagData)
      .catch(() => setPagData({ items: [], total: 0, total_pages: 0 }));
  };

  useEffect(() => {
    myApi.comissoesResumo().then(setResumo).catch(() => setResumo([]));
    myApi.comissoesAcumulado().then(setAcum).catch(() => setAcum({}));
  }, []);

  useEffect(() => {
    setPagData(null);
    myApi.comissoesPagamentos(pagPage, pagSize)
      .then(setPagData)
      .catch(() => setPagData({ items: [], total: 0, total_pages: 0 }));
  }, [pagPage]);

  const openTransfer = () => {
    setTransferValor("");
    setTransferError("");
    setTransferSuccess("");
    setTransferOpen(true);
  };
  const closeTransfer = () => {
    if (transferLoading) return;
    setTransferOpen(false);
  };
  const submitTransfer = async (e) => {
    e?.preventDefault?.();
    setTransferError("");
    setTransferSuccess("");
    const valor = Number(String(transferValor).replace(",", "."));
    const max = acum?.disponivel_pagamento || 0;
    if (!valor || valor <= 0) { setTransferError("Indique um valor positivo."); return; }
    if (valor > max) { setTransferError(`Máximo disponível: ${fmt(max)}.`); return; }
    setTransferLoading(true);
    try {
      const r = await myApi.comissoesTransferirCreditos(valor);
      setTransferSuccess(`Transferidos ${fmt(r.valor)} para créditos.`);
      setTransferLoading(false);
      // Refresh all data so the credit balance + disponivel update on screen
      refreshAll();
      // Auto-close after 1.6s
      setTimeout(() => setTransferOpen(false), 1600);
    } catch (err) {
      setTransferError(err?.message || "Erro ao transferir. Tente novamente.");
      setTransferLoading(false);
    }
  };

  const pagamentos = pagData?.items || [];
  const pagTotal = pagData?.total ?? 0;
  const pagTotalPages = pagData?.total_pages ?? 0;

  return (
    <div data-testid="my-commissions-page" className="space-y-6">
      <div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Comissões
        </h1>
        <p className="text-sm text-[#6B7280]">
          Valores acumulados, créditos disponíveis, pagamentos recebidos e resumo mensal.
        </p>
      </div>

      {/* Row 1: headline totals */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#0F1829] to-[#1a2542] text-white rounded-[20px] p-6" data-testid="my-comm-total">
          <div className="flex items-center gap-2 mb-2">
            <CircleDollarSign size={14} className="text-[#D1CFC9]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Total Acumulado</div>
          </div>
          <div className="text-4xl" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmt(acum.total_acumulado) : "—"}
          </div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-6" data-testid="my-comm-pago">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={14} className="text-[#6F7559]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">Já Pago</div>
          </div>
          <div className="text-4xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmt(acum.total_pago) : "—"}
          </div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-6" data-testid="my-comm-porreceber">
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank size={14} className="text-[#6F4F1D]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">Por Receber</div>
          </div>
          <div className="text-4xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmt(acum.por_receber) : "—"}
          </div>
          {acum?.disponivel_pagamento > 0 && (
            <>
              <div className="text-xs text-[#4A5E2E] mt-2 inline-flex items-center gap-1">
                <Coins size={12} /> {fmt(acum.disponivel_pagamento)} disponível para pagamento
              </div>
              <button
                type="button"
                onClick={openTransfer}
                data-testid="my-comm-btn-transfer-credits"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-xs font-semibold uppercase tracking-[0.12em] text-white bg-[#0F1829] hover:bg-[#1a2542] transition"
              >
                <ArrowRightLeft size={13} /> Transferir para créditos
              </button>
            </>
          )}
        </div>
      </div>

      {/* Row 2: Créditos + NetPoints (like CRM) */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#6F4F1D] to-[#978D78] text-white rounded-[20px] p-5" data-testid="my-comm-creditos">
          <div className="flex items-center gap-2 mb-2">
            <Coins size={14} className="text-[#D1CFC9]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">Créditos Disponíveis</div>
          </div>
          <div className="text-3xl" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmtNum(acum.creditos) : "—"}
          </div>
          <div className="text-xs text-white/60 mt-1">pontos acumulados</div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-5" data-testid="my-comm-np-pessoal">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={14} className="text-[#978D78]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">NetPoints Pessoal</div>
          </div>
          <div className="text-2xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmtNum(acum.netpoints_pessoal) : "—"}
          </div>
          <div className="text-[10px] text-[#6B7280] mt-1">mês actual</div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-5" data-testid="my-comm-np-cliente">
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-[#978D78]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">NetPoints Cliente</div>
          </div>
          <div className="text-2xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmtNum(acum.netpoints_cliente) : "—"}
          </div>
          <div className="text-[10px] text-[#6B7280] mt-1">mês actual</div>
        </div>
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-5" data-testid="my-comm-np-rede">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={14} className="text-[#978D78]" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">NetPoints Rede</div>
          </div>
          <div className="text-2xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {acum ? fmtNum(acum.netpoints_rede) : "—"}
          </div>
          <div className="text-[10px] text-[#6B7280] mt-1">mês actual</div>
        </div>
      </div>

      {/* Resumo mensal */}
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden" data-testid="my-comm-resumo">
        <div className="px-6 py-4 border-b border-[#F5F2EB]">
          <h2 className="text-lg text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            Resumo Mensal
          </h2>
        </div>
        {resumo === null ? (
          <div className="p-10 text-center text-sm text-[#978D78]">A carregar...</div>
        ) : resumo.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#4B5563]">Sem comissões registadas.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#F5F2EB]">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Período</th>
                <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F2EB]">
              {resumo.map((r) => (
                <tr key={`${r.Ano}-${r.Mes}`} className="hover:bg-[#FAF8F3]">
                  <td className="px-5 py-3 text-sm text-[#0F1829]">
                    <span className="inline-flex items-center gap-2">
                      <Calendar size={13} className="text-[#978D78]" /> {MONTHS[r.Mes - 1]} {r.Ano}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-sm text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                    {fmt(r.TotalMes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagamentos */}
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden" data-testid="my-comm-pagamentos">
        <div className="px-6 py-4 border-b border-[#F5F2EB] flex items-center justify-between">
          <h2 className="text-lg text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            Pagamentos Efectuados
          </h2>
          <span className="text-xs text-[#978D78]">{pagTotal} pagamentos</span>
        </div>
        {pagData === null ? (
          <div className="p-10 text-center text-sm text-[#978D78]">A carregar...</div>
        ) : pagamentos.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#4B5563]">Sem pagamentos registados.</div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-[#F5F2EB]">
                <tr>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Data</th>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Tipo</th>
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Observações</th>
                  <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F2EB]">
                {pagamentos.map((p) => (
                  <tr key={p.ID_ComissaoPagamento} data-testid={`my-comm-pag-${p.ID_ComissaoPagamento}`} className="hover:bg-[#FAF8F3]">
                    <td className="px-5 py-3 text-sm text-[#4B5563]">{(p.DataRegisto || "").slice(0, 10)}</td>
                    <td className="px-5 py-3 text-sm text-[#4B5563]">{p.TipoPagamento || p.ID_TipoPagamento || "—"}</td>
                    <td className="px-5 py-3 text-xs text-[#6B7280] max-w-sm truncate">{p.Observacoes || "—"}</td>
                    <td className="px-5 py-3 text-right text-sm text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                      {fmt(p.ValorPago)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={pagPage} totalPages={pagTotalPages} onChange={setPagPage} testid="my-comm-pag-pagination" />
          </>
        )}
      </div>

      {transferOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4"
          onClick={closeTransfer}
          data-testid="my-comm-transfer-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-[20px] shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E1D6] bg-[#F5F2EB]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Conversão de Comissões</p>
                <p className="text-base font-semibold text-[#0F1829]">Transferir para créditos</p>
              </div>
              <button onClick={closeTransfer} className="text-[#6B7280] hover:text-[#0F1829]" disabled={transferLoading}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submitTransfer} className="p-5 space-y-4">
              <p className="text-sm text-[#4B5563]">
                Vai converter parte das suas comissões em créditos disponíveis para utilizar em encomendas.
                Esta operação é irreversível.
              </p>
              <div className="bg-[#FAF8F3] border border-[#E6E1D6] rounded-lg p-3 flex items-center justify-between">
                <span className="text-xs text-[#6B7280] uppercase tracking-[0.15em]">Disponível</span>
                <span className="text-base font-bold text-[#0F1829]">{fmt(acum?.disponivel_pagamento || 0)}</span>
              </div>
              <div>
                <label htmlFor="transfer-amount" className="block text-[10px] uppercase tracking-[0.15em] text-[#978D78] mb-1">
                  Valor a transferir (€)
                </label>
                <input
                  id="transfer-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={acum?.disponivel_pagamento || 0}
                  value={transferValor}
                  onChange={(e) => setTransferValor(e.target.value)}
                  data-testid="my-comm-transfer-amount"
                  disabled={transferLoading}
                  autoFocus
                  className="w-full px-3 py-2.5 text-base border border-[#E6E1D6] rounded-lg outline-none focus:border-[#0F1829] font-mono"
                  placeholder="0.00"
                />
                <button
                  type="button"
                  onClick={() => setTransferValor(String(acum?.disponivel_pagamento || 0))}
                  className="text-xs text-[#0F1829] underline mt-1"
                  data-testid="my-comm-transfer-max"
                  disabled={transferLoading}
                >
                  Usar máximo
                </button>
              </div>

              {transferError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="my-comm-transfer-error">
                  {transferError}
                </div>
              )}
              {transferSuccess && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2" data-testid="my-comm-transfer-success">
                  ✓ {transferSuccess}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeTransfer}
                  disabled={transferLoading}
                  className="px-4 py-2 text-sm rounded-full text-[#0F1829] hover:bg-[#F5F2EB]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={transferLoading}
                  data-testid="my-comm-transfer-submit"
                  className="px-5 py-2 text-sm font-semibold rounded-full text-white bg-[#0F1829] hover:bg-[#1a2542] transition disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {transferLoading && <Loader2 size={14} className="animate-spin" />}
                  Confirmar transferência
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
